import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { FlowBreadcrumbs, FlowFormCard, SubmitButton, mediaPreviewStyles } from './FlowLayout';
import { fetchDestinationHierarchy } from './flowHierarchy';

const AddEditSpecialFeature = () => {
  const navigate = useNavigate();
  const { id, tourTypeId } = useParams();
  const [hierarchy, setHierarchy] = useState({
    destinationName: '',
    tourTypeId: '',
    tourTypeName: '',
    continentId: '',
    continentName: ''
  });
  const [form, setForm] = useState({
    type: '',
    title: '',
    description: '',
    thumbnail: null,
    promote: false,
    website_url: '',
    imagePreview: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDestinationHierarchy(tourTypeId)
      .then((response) => setHierarchy(response))
      .catch((error) => console.error('There was an error fetching the destination context!', error));

    if (id) {
      axios.get(`https://aaryanholidays.in/backend/special-features/${id}`)
        .then((response) => {
          const feature = response.data;
          setForm({
            type: feature.type,
            title: feature.title,
            description: feature.description,
            thumbnail: null,
            promote: Boolean(feature.promote),
            website_url: feature.website_url,
            imagePreview: feature.thumbnail ? `https://aaryanholidays.in/express/uploads/${feature.thumbnail}` : ''
          });
        })
        .catch((error) => console.error('There was an error fetching the special feature!', error));
    }
  }, [id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append('type', form.type);
    formData.append('title', form.title);
    formData.append('description', form.description);
    formData.append('tour_type_id', tourTypeId);
    formData.append('website_url', form.website_url);
    formData.append('promote', form.promote ? '1' : '0');
    if (form.thumbnail) {
      formData.append('thumbnail', form.thumbnail);
    }

    try {
      if (id) {
        await axios.put(`https://aaryanholidays.in/backend/special-features/${id}`, formData);
      } else {
        await axios.post('https://aaryanholidays.in/backend/special-features', formData);
      }
      navigate(`/manage-special-features/${tourTypeId}`);
    } catch (error) {
      console.error('There was an error saving the special feature!', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <FlowBreadcrumbs items={[{ label: 'Tour Management', to: '/manage-continents' }, { label: hierarchy.continentName || 'Continent', to: hierarchy.continentId ? `/manage-tour-type/${hierarchy.continentId}` : undefined }, { label: hierarchy.tourTypeName || 'Country', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined }, { label: hierarchy.destinationName || 'Package', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined }, { label: 'Special Features', to: `/manage-special-features/${tourTypeId}` }, { label: id ? 'Edit Feature' : 'Add Feature' }]} />
      <FlowFormCard title={id ? 'Edit Special Feature' : 'Add Special Feature'} subtitle="Create polished supporting cards that strengthen package storytelling and homepage promotion.">
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type" onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <MenuItem value="Hotel">Hotel</MenuItem>
                <MenuItem value="Shopping Mall">Shopping Mall</MenuItem>
                <MenuItem value="Kids Arena">Kids Arena</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel control={<Checkbox checked={form.promote} onChange={(e) => setForm({ ...form, promote: e.target.checked })} />} label="Promote to home page" />
            <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth required />
            <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth multiline rows={5} required />
            <Box>
              <input id="special-feature-upload" type="file" hidden onChange={(e) => {
                const file = e.target.files[0];
                setForm({ ...form, thumbnail: file, imagePreview: file ? URL.createObjectURL(file) : form.imagePreview });
              }} />
              <label htmlFor="special-feature-upload">
                <SubmitButton component="span" variant="outlined">
                  Upload Thumbnail
                </SubmitButton>
              </label>
            </Box>
            {form.imagePreview ? <img src={form.imagePreview} alt="Special feature preview" style={mediaPreviewStyles} /> : null}
            <TextField label="Website URL" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} fullWidth />
            <SubmitButton type="submit" variant="contained" loading={submitting}>
              {id ? 'Update Feature' : 'Create Feature'}
            </SubmitButton>
          </Stack>
        </form>
      </FlowFormCard>
    </Box>
  );
};

export default AddEditSpecialFeature;
